import React, { useMemo, useRef, useState } from 'react';
import { Button, Card, Pill, Progress } from '../components/Primitives';
import { Icon } from '../components/Icon';
import { useApp } from '../lib/app-context';
import { canAccess, deterministicShuffle, dynamicLesson, uiLabel } from '../lib/learning';
import { haptic } from '../lib/telegram';
import { WORDS } from '../content/curriculum';
import { grammarNote } from '../content/grammar';

function speak(text: string, rate = .9) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-US';
  utterance.rate = rate;
  window.speechSynthesis.speak(utterance);
}

function normalizeAnswer(value: string) {
  return value
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[’‘`]/g, "'")
    .replace(/[^a-z0-9' ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

type Phase = 'intro' | 'input' | 'recognition' | 'production' | 'grammar' | 'result';

export function Lesson() {
  const { state, answer, answerSkill, finishLesson, setRoute } = useApp();
  const allowed = canAccess(state, 'lesson');
  const lesson = dynamicLesson(state);
  const sequence = useMemo(
    () => deterministicShuffle([...lesson.newWords, ...lesson.reviewWords], `mission-${state.progress.day}-${state.progress.courseDate}`).slice(0, 12),
    [lesson.newWords, lesson.reviewWords, state.progress.day, state.progress.courseDate],
  );
  const recognitionWords = useMemo(() => sequence.slice(0, Math.min(6, sequence.length)), [sequence]);
  const productionWords = useMemo(() => sequence.slice(0, Math.min(4, sequence.length)), [sequence]);
  const note = grammarNote(lesson.grammar);
  const grammarOptions = useMemo(
    () => deterministicShuffle(note.options, `grammar-${state.progress.day}-${note.title}`),
    [note, state.progress.day],
  );

  const [phase, setPhase] = useState<Phase>('intro');
  const [inputIndex, setInputIndex] = useState(0);
  const [recognitionIndex, setRecognitionIndex] = useState(0);
  const [productionIndex, setProductionIndex] = useState(0);
  const [recognitionCorrect, setRecognitionCorrect] = useState(0);
  const [productionCorrect, setProductionCorrect] = useState(0);
  const [grammarCorrect, setGrammarCorrect] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [typedResult, setTypedResult] = useState<'correct' | 'wrong' | null>(null);
  const [grammarPicked, setGrammarPicked] = useState<string | null>(null);
  const shownAt = useRef(Date.now());

  if (!allowed) return <div className="screen"><Card className="locked-card"><Icon name="lock" size={38}/><h2>365 kunlik kurs — PRO</h2><p>7 kunlik sinov tugagan. So‘zlar va basic SRS bepul qoladi; to‘liq Daily Mission PRO bilan davom etadi.</p><Button onClick={() => setRoute('pricing')}>PRO imkoniyatlarini ko‘rish</Button></Card></div>;
  if (!sequence.length) return <div className="screen"><Card><h2>Bugungi missiya tayyorlanmadi</h2><p>Kontent paketi yoki sync holatini tekshiring.</p><Button onClick={() => setRoute('dashboard')}>Bosh sahifa</Button></Card></div>;

  const currentInput = sequence[inputIndex] ?? sequence[0];
  const currentInputIsNew = lesson.newWords.some((word) => word.id === currentInput?.id);
  const recognitionWord = recognitionWords[recognitionIndex] ?? recognitionWords[0];
  const productionWord = productionWords[productionIndex] ?? productionWords[0];
  const listeningPrompt = recognitionIndex % 2 === 1;
  const options = recognitionWord ? deterministicShuffle([
    recognitionWord.uz,
    ...deterministicShuffle(WORDS.filter((word) => word.id !== recognitionWord.id), `pool-${recognitionWord.id}`)
      .map((word) => word.uz)
      .filter((value, index, all) => all.indexOf(value) === index && value !== recognitionWord.uz)
      .slice(0, 3),
  ], `opt-${recognitionWord.id}-${state.progress.day}`) : [];

  const progressValue = phase === 'intro' ? 4
    : phase === 'input' ? 8 + ((inputIndex + 1) / sequence.length) * 22
    : phase === 'recognition' ? 30 + ((recognitionIndex + 1) / Math.max(1, recognitionWords.length)) * 28
    : phase === 'production' ? 58 + ((productionIndex + 1) / Math.max(1, productionWords.length)) * 22
    : phase === 'grammar' ? 88
    : 100;

  const recognitionScore = recognitionWords.length ? recognitionCorrect / recognitionWords.length : 0;
  const productionScore = productionWords.length ? productionCorrect / productionWords.length : 0;
  const finalScore = Math.round((recognitionScore * .45 + productionScore * .35 + grammarCorrect * .20) * 100);

  const chooseRecognition = (option: string) => {
    if (selected || !recognitionWord) return;
    setSelected(option);
    const ok = option === recognitionWord.uz;
    const latency = Date.now() - shownAt.current;
    answer(recognitionWord, ok, latency, listeningPrompt ? 'listening' : 'meaning_recall');
    if (ok) setRecognitionCorrect((value) => value + 1);
    haptic(ok ? 'light' : 'medium');
  };

  const nextRecognition = () => {
    setSelected(null);
    if (recognitionIndex >= recognitionWords.length - 1) {
      setPhase('production');
      setProductionIndex(0);
    } else {
      setRecognitionIndex((value) => value + 1);
      shownAt.current = Date.now();
    }
  };

  const checkProduction = () => {
    if (typedResult || !productionWord) return;
    const ok = normalizeAnswer(typed) === normalizeAnswer(productionWord.en);
    setTypedResult(ok ? 'correct' : 'wrong');
    answer(productionWord, ok, Date.now() - shownAt.current, 'form_recall');
    if (ok) setProductionCorrect((value) => value + 1);
    haptic(ok ? 'light' : 'medium');
  };

  const nextProduction = () => {
    setTyped('');
    setTypedResult(null);
    if (productionIndex >= productionWords.length - 1) {
      setPhase('grammar');
    } else {
      setProductionIndex((value) => value + 1);
      shownAt.current = Date.now();
    }
  };

  const chooseGrammar = (option: string) => {
    if (grammarPicked) return;
    setGrammarPicked(option);
    const ok = option === note.answer;
    setGrammarCorrect(ok ? 1 : 0);
    answerSkill('grammar', ok, note.title);
  };

  const finishGrammar = () => setPhase('result');
  const mix = lesson.memoryMix;

  return <div className="screen lesson-screen">
    <div className="lesson-top">
      <button className="icon-button" onClick={() => setRoute('dashboard')} aria-label="Darsni yopish"><Icon name="x"/></button>
      <div><span>DAY {state.progress.day} · {state.progress.courseDate}</span><Progress value={progressValue}/></div>
      <Pill tone={state.progress.courseFinished || state.progress.todayMissionCompleted ? 'green' : lesson.recoveryMode ? 'gold' : lesson.isBoss ? 'gold' : 'blue'}>
        {state.progress.courseFinished ? 'MAINTENANCE · FLUENCY' : state.progress.todayMissionCompleted ? 'BUGUN BAJARILGAN' : lesson.recoveryMode ? 'RECOVERY + MISSION' : lesson.isBoss ? 'BOSS DAY' : 'DAILY MISSION'}
      </Pill>
    </div>

    {phase === 'intro' && <Card className="lesson-stage intro-stage">
      <span className="big-icon"><Icon name={lesson.recoveryMode ? 'repeat' : lesson.isBoss ? 'trophy' : 'book'} size={34}/></span>
      <span className="eyebrow">{lesson.level} · {lesson.topic.toUpperCase()}</span>
      <h1>{uiLabel(state, lesson.titleUz, lesson.titleEn)}</h1>
      <p>{lesson.recoveryMode ? 'Bugungi yangi material xotira qarziga mos kamaytirildi. Zaif bilimlar yangi kontekstda qayta chaqiriladi.' : lesson.goalUz}</p>
      <div className="lesson-outline">
        <span><Icon name="cards"/> {lesson.newWords.length} yangi input</span>
        <span><Icon name="repeat"/> {lesson.reviewWords.length} spiral recall</span>
        <span><Icon name="grammar"/> {lesson.grammar}</span>
      </div>
      {mix && <div className="memory-mix">{Boolean(mix.recovery) && <Pill tone="gold">Recovery {mix.recovery}</Pill>}<Pill>Due {mix.due}</Pill><Pill tone="red">Rebuild {mix.rebuild}</Pill><Pill tone="blue">15–60 {mix.active}</Pill><Pill tone="gold">Deep {mix.deep}</Pill></div>}
      {state.progress.courseFinished ? <div className="status-message">365 kunlik asosiy yo‘l yakunlangan. Bu mashq maintenance uchun: Day va asosiy daily completion XP oshmaydi.</div> : state.progress.todayMissionCompleted && <div className="status-message">Bugungi asosiy missiya allaqachon bajarilgan. Qayta ishlashingiz mumkin, ammo kun va asosiy completion XP qayta berilmaydi.</div>}
      <Button onClick={() => setPhase('input')}>Boshlash <Icon name="arrow"/></Button>
    </Card>}

    {phase === 'input' && <Card className="lesson-stage word-stage">
      <div className="word-progress"><span>{inputIndex + 1}/{sequence.length}</span><Pill tone={currentInputIsNew ? 'green' : 'blue'}>{currentInputIsNew ? 'MEANING-FOCUSED INPUT' : 'SPIRAL / RECOVERY INPUT'}</Pill></div>
      <button className="audio-button" onClick={() => speak(currentInput.en)} aria-label="Eshitish"><Icon name="headphones" size={26}/></button>
      <h1>{currentInput.en}</h1><div className="word-translation">{currentInput.uz}</div><span className="word-pos">{currentInput.pos} · {currentInput.level}</span>
      <div className="example-box"><strong>{currentInput.exampleEn}</strong><span>{currentInput.exampleUz}</span></div>
      <div className="lesson-nav"><Button variant="ghost" disabled={inputIndex === 0} onClick={() => setInputIndex((value) => value - 1)}>Orqaga</Button><Button onClick={() => { if (inputIndex >= sequence.length - 1) { setPhase('recognition'); setRecognitionIndex(0); shownAt.current = Date.now(); } else setInputIndex((value) => value + 1); }}>{inputIndex >= sequence.length - 1 ? 'Recall boshlash' : 'Keyingi'}</Button></div>
    </Card>}

    {phase === 'recognition' && recognitionWord && <Card className="lesson-stage quiz-stage">
      <div className="word-progress"><span>{recognitionIndex + 1}/{recognitionWords.length}</span><Pill tone="gold">{listeningPrompt ? 'LISTENING RECALL' : 'MEANING RECALL'}</Pill></div>
      <span className="eyebrow">JAVOBNI XOTIRADAN CHIQARING</span>
      {listeningPrompt ? <><button className="audio-button" onClick={() => speak(recognitionWord.en, .86)} aria-label="So‘zni eshitish"><Icon name="headphones" size={28}/></button><h2>Eshitgan so‘zingiz ma’nosini tanlang.</h2></> : <h1>{recognitionWord.en}</h1>}
      <div className="quiz-options">{options.map((option) => { const cls = selected ? option === recognitionWord.uz ? 'correct' : option === selected ? 'wrong' : '' : ''; return <button key={option} className={cls} onClick={() => chooseRecognition(option)} disabled={Boolean(selected)}>{option}{cls === 'correct' && <Icon name="check"/>}{cls === 'wrong' && <Icon name="x"/>}</button>; })}</div>
      {selected && <div className="lesson-nav"><Button onClick={nextRecognition}>Keyingi</Button></div>}
    </Card>}

    {phase === 'production' && productionWord && <Card className="lesson-stage quiz-stage">
      <div className="word-progress"><span>{productionIndex + 1}/{productionWords.length}</span><Pill tone="blue">FORM RECALL</Pill></div>
      <span className="eyebrow">UZ → EN · JAVOBNI O‘ZINGIZ YOZING</span>
      <h1>{productionWord.uz}</h1>
      <label>Inglizcha javob<input autoFocus value={typed} disabled={Boolean(typedResult)} onChange={(event) => setTyped(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && typed.trim()) checkProduction(); }} placeholder="Inglizcha yozing..." autoComplete="off" spellCheck={false}/></label>
      {typedResult && <div className={`production-feedback ${typedResult}`}><strong>{typedResult === 'correct' ? 'To‘g‘ri' : `To‘g‘ri javob: ${productionWord.en}`}</strong><span>{productionWord.exampleEn}</span></div>}
      <div className="lesson-nav">{!typedResult ? <Button disabled={!typed.trim()} onClick={checkProduction}>Tekshirish</Button> : <Button onClick={nextProduction}>Keyingi</Button>}</div>
    </Card>}

    {phase === 'grammar' && <Card className="lesson-stage quiz-stage">
      <Pill tone="blue">GRAMMAR IN CONTEXT</Pill><h2>{note.title}</h2><p>{note.explanationUz}</p><div className="example-box"><strong>{note.pattern}</strong><span>Form · Meaning · Use</span></div><h2>{note.question}</h2>
      <div className="quiz-options">{grammarOptions.map((option) => { const cls = grammarPicked ? option === note.answer ? 'correct' : option === grammarPicked ? 'wrong' : '' : ''; return <button key={option} className={cls} disabled={Boolean(grammarPicked)} onClick={() => chooseGrammar(option)}>{option}{cls === 'correct' && <Icon name="check"/>}{cls === 'wrong' && <Icon name="x"/>}</button>; })}</div>
      {grammarPicked && <div className="lesson-nav"><Button onClick={finishGrammar}>Natijani ko‘rish</Button></div>}
    </Card>}

    {phase === 'result' && <Card className="lesson-stage result-stage">
      <div className={`score-ring ${finalScore >= 75 ? 'pass' : 'fail'}`}><strong>{finalScore}%</strong><span>Evidence</span></div>
      <h1>{state.progress.courseFinished ? 'Maintenance mashqi yakunlandi' : finalScore >= 75 ? 'Bugungi missiya bajarildi' : 'Bugungi bilimni mustahkamlaymiz'}</h1>
      <p>{state.progress.courseFinished ? '365 kunlik kalendar yo‘li yakunlangan. Natija mastery evidence’ga qo‘shiladi, ammo Day yoki daily completion oshmaydi.' : finalScore >= 75 ? `Day ${state.progress.day} yakunlandi. Day raqami dars tugagani uchun emas, haqiqiy kalendar sanasi o‘zgarganda yangilanadi.` : `Day ${state.progress.day} o‘zgarmaydi. Zaif atomlar Memory Forge va keyingi kontekstlarda qaytadi.`}</p>
      <div className="result-metrics"><span><b>{recognitionCorrect}/{recognitionWords.length}</b> recognition</span><span><b>{productionCorrect}/{productionWords.length}</b> production</span><span><b>{grammarCorrect ? '1/1' : '0/1'}</b> grammar</span></div>
      <div className="daily-actions"><Button onClick={() => { finishLesson(finalScore); setRoute(finalScore >= 75 ? 'dashboard' : 'review'); }}>{finalScore >= 75 ? 'Yakunlash' : 'Recovery boshlash'}</Button><Button variant="secondary" onClick={() => setRoute('skills')}>Skills Lab</Button></div>
    </Card>}
  </div>;
}
